import React, { useState, useMemo, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';
import ChassisDropdown from '../../../../components/common/ChassisWithExternalDropdown';
import BulletinNoDropdown from '../../../../components/common/BulletinNoDropdown';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';


export default function ServiceBulletinTable({ 
    items, 
    totalCount, 
    loading, 
    lazyParams, 
    onPage, 
    columns,
    sortField,
    sortOrder,
    onSortChange,
    activeFilters,
    onFiltersChange,
    onResetFilters,
    chassisNo          
}) {
    const { th } = useLanguage();
    const dataTableRef = useRef(null);
    const navigate = useNavigate();

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return Number(value).toLocaleString('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const rows = Array.isArray(items)
        ? items.map((item, index) => ({ ...item, _rowId: `${lazyParams.first}-${index}` }))
        : [];

    const handleSort = (e) => {
        onSortChange?.(e);
    };
    const tableFilters = useMemo(
        () => ({
            qmnum: {
                value: activeFilters?.qmnum ?? null,
                matchMode: FilterMatchMode.EQUALS
            },
            bulletinName: {
                value: activeFilters?.bulletinName ?? null,
                matchMode: activeFilters?.bulletinNameMatchMode ?? FilterMatchMode.CONTAINS
            },
            bulletinDescription: {
                value: activeFilters?.bulletinDescription ?? null,
                matchMode: activeFilters?.bulletinDescriptionMatchMode ?? FilterMatchMode.CONTAINS
            },
            isApplied: {
                value: activeFilters?.isApplied ?? null,
                matchMode: FilterMatchMode.EQUALS
            }
        }),
        [activeFilters]
    );

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};

        const updatedFilters = {
            ...(activeFilters || {}),
            qmnum: 'qmnum' in filters ? filters.qmnum?.value ?? null : activeFilters?.qmnum ?? null,
            qmnumMatchMode:  FilterMatchMode.EQUALS,
            bulletinName: 'bulletinName' in filters ? filters.bulletinName?.value ?? null : activeFilters?.bulletinName ?? null,
            bulletinNameMatchMode: 'bulletinName' in filters ? filters.bulletinName?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.bulletinNameMatchMode ?? FilterMatchMode.CONTAINS,
            bulletinDescription: 'bulletinDescription' in filters ? filters.bulletinDescription?.value ?? null : activeFilters?.bulletinDescription ?? null,
            bulletinDescriptionMatchMode: 'bulletinDescription' in filters ? filters.bulletinDescription?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.bulletinDescriptionMatchMode ?? FilterMatchMode.CONTAINS,
            isApplied: 'isApplied' in filters ? (filters.isApplied?.value ?? null) : (activeFilters?.isApplied ?? null)
    };

        onFiltersChange?.(updatedFilters);
    };

    const actionFilterTemplate = () => {
        return (
            <div className="flex justify-content-center align-items-center">
                <i 
                    onClick={onResetFilters} 
                    className="pi pi-filter-slash cursor-pointer text-blue-500 hover:text-blue-700" 
                    style={{ color: '#0B3187', fontSize: '1rem' }} 
                    title={th('filter.reset')}
                ></i>
            </div>
        );
    };

     // Bulletin No dropdown filter template
    const bulletinNoFilterTemplate = (options) => {
        return (
            <BulletinNoDropdown
                value={options.value}
                onChange={(bulletinNo) => options.filterApplyCallback(bulletinNo)}
                className="p-column-filter"
                placeholder={th('common.search')}
            />
        );
    };

    // Chassis dropdown filter template
    const chassisFilterTemplate = (options) => {
        return (
           <div style={{ width: '12rem' }}>
            <ChassisDropdown
                value={options.value}
                onChange={(chassisId) => options.filterApplyCallback(chassisId)}
                className="p-column-filter"
                showDescription={false}
            />
        </div>
        );
    };

    const createTextColumn = (field, headerKey, options = {}) => {
        const { filterField = field, body, sortable = true, minWidth = '180px', key = field, frozen = false } = options;

        return (
            <Column
                key={key}
                sortable={sortable}
                field={field}
                header={th(headerKey)}
                style={{ minWidth }}
                body={body}
                filter
                filterField={filterField}
                filterPlaceholder={th('common.search')}
                showFilterMenu={true}
                filterMatchModeOptions={textMatchModeOptions}
                showClearButton={false}
                frozen={frozen}
            />
        );
    };

    const createDropdownColumn = (field, headerKey, filterElement, options = {}) => {
        const { filterField = field, body, sortable = true, minWidth = '180px', key = field, showFilterMenu = false } = options;

        return (
            <Column 
                key={key} 
                sortable={sortable} 
                field={field} 
                header={th(headerKey)} 
                style={{ minWidth }} 
                body={body} 
                filter 
                filterField={filterField} 
                filterElement={filterElement} 
                showFilterMenu={showFilterMenu} 
            />
        );
    };
    const handleShowDetail = (bulletinNo) => {
    window.open(`/warranty-management/bulletins/service-bulletin-report/${bulletinNo}`, '_blank', 'noopener,noreferrer');
};

    const actionBodyTemplate = (rowData) => {
        return (
            <Button
                icon="pi pi-eye"
                className="p-button-rounded p-button-text p-button-sm"
                onClick={() => handleShowDetail(rowData.qmnum)}
                tooltip={th('serviceBulletinReport.tooltips.viewDetail')}
                tooltipOptions={{ position: 'top' }}
                style={{ color: '#0B3187' }}
            />
        );
    };

    const isAppliedBodyTemplate = (rowData) => {
        const applied = rowData?.isApplied === true;
        return (
            <span
                style={{
                    color: applied ? '#15803d' : '#dc2626',
                    fontWeight: 600
                }}
            >
                {applied ? th('serviceBulletinReport.columns.applied') : th('serviceBulletinReport.columns.notApplied')}
            </span>
        );
    };

    const isAppliedOptions = useMemo(
        () => [
            { label: th('common.all'), value: null },
            { label: th('serviceBulletinReport.columns.applied'), value: true },
            { label: th('serviceBulletinReport.columns.notApplied'), value: false }
        ],
        [th]
    );

    const isAppliedFilterTemplate = (options) => {
        return (
            <Dropdown
                value={options.value ?? null}
                options={isAppliedOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                className="p-column-filter"
                placeholder={th('common.all')}
                style={{ width: '100%', minWidth: '8rem' }}
                showClear={false}
            />
        );
    };
   

    return (
        <DataTable
            value={rows}
            dataKey="_rowId"
            lazy
            paginator
            rows={lazyParams.rows}
            first={lazyParams.first}
            totalRecords={totalCount}
            onPage={onPage}
            loading={loading}
            emptyMessage={th('common.noRecords')}
            rowsPerPageOptions={[10, 25, 50]}
            showGridlines
            tableStyle={{ minWidth: '50rem' }}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            ref={dataTableRef}
        >
            <Column
                key="action"
                body={actionBodyTemplate}
                bodyClassName="text-center"
                headerClassName="text-center"
                exportable={false}
                style={{ width: '50px', minWidth: '50px' }}
                sortable={false}
                filter={true}
                filterElement={actionFilterTemplate}
                showFilterMenu={false}
                showClearButton={false}
            />
            {createDropdownColumn('qmnum', 'serviceBulletinReport.columns.bulletinNumber', bulletinNoFilterTemplate, {
                minWidth: '100px',
                width: '100px',
                body: (row) => row?.qmnum || '-'
            })}
            {createTextColumn('bulletinName', 'serviceBulletinReport.columns.bulletinName', {
                minWidth: '120px',
                width: '120px',
                body: (row) => createTruncatedTextForTable(row?.bulletinName ? String(row.bulletinName) : '-', 80)
            })}
            {createTextColumn('bulletinDescription', 'serviceBulletinReport.columns.bulletinDescription', {
                minWidth: '120px',
                body: (row) => createTruncatedTextForTable(row?.bulletinDescription ? String(row.bulletinDescription) : '-', 80)
            })}
            {chassisNo ? (
                <Column
                    key="isApplied"
                    field="isApplied"
                    header={th('serviceBulletinReport.columns.applicationStatus')}
                    body={isAppliedBodyTemplate}
                    bodyClassName="text-center"
                    headerClassName="text-center"
                    sortable={false}
                    style={{ minWidth: '140px' }}
                    filter
                    filterField="isApplied"
                    filterElement={isAppliedFilterTemplate}
                    showFilterMenu={false}
                    showClearButton={false}
                />
            ) : null}
        </DataTable>
    );
}